import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { Component, PlateAnalysis } from '@paragon/contract-library';
import { designService, PlateAnalysisResult } from '@paragon/data-connections';
import { isPlateAnalysisSupported } from '@paragon/material-lists/lib/plateUtilities';

import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { AsyncThunkActionVoid } from '../actions/thunkActions';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { setRunPlateMislocations } from './applicationSlice';
import { undoComponentDesign } from './editorSlice';

export type LivePlatingState = Record<string, PlateAnalysisState | undefined>;

export type PlateAnalysisState =
  | { readonly status: 'pending'; readonly timestamp: string }
  | { readonly status: 'error'; readonly timestamp: string }
  | { readonly status: 'complete'; readonly data: PlateAnalysis; readonly timestamp: string };

export const analyzeSelectedPlates =
  (selectedPlateGuids?: string[], customComponent?: Component): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    let componentDesign = getActiveComponentDesign(state);
    if (componentDesign == null) return;
    if (customComponent) {
      componentDesign = { ...componentDesign, component: customComponent };
    }

    const selectedGuids =
      selectedPlateGuids ??
      getSelectedItemIdentifiers(state)
        .filter((item) => item.type === 'platePair')
        .map((plate) => plate.guid);

    if (
      selectedGuids.length === 0 ||
      componentDesign.component.platePairs.some((platePair) => !isPlateAnalysisSupported(platePair))
    )
      return;

    const timestamp = new Date().toISOString();
    dispatch(livePlatingSlice.actions.receivePending({ platePairGuids: selectedGuids, timestamp }));

    const runPlateMislocations = state.application.runPlateMislocations;

    let analysis: PlateAnalysisResult;
    try {
      analysis = await designService.componentDesigns.postAnalyzePlates(
        componentDesign,
        selectedGuids,
        runPlateMislocations,
      );
    } catch {
      // It's expected that plate analysis will fail sometimes.
      dispatch(livePlatingSlice.actions.receiveError({ platePairGuids: selectedGuids, timestamp }));
      return;
    }

    dispatch(
      livePlatingSlice.actions.receiveAnalysis({
        analysis,
        timestamp,
      }),
    );
  };

const emptyState: LivePlatingState = {};

const livePlatingSlice = createSlice({
  name: 'livePlating',
  initialState: emptyState,
  reducers: {
    receivePending(state, action: PayloadAction<{ platePairGuids: string[]; timestamp: string }>) {
      for (const platePairGuid of action.payload.platePairGuids) {
        if (isPlateAnalysisStale(state, platePairGuid, action.payload.timestamp)) continue;

        // if live plating already ran for this plate, leave it alone until the fresh analysis completes
        if (state[platePairGuid]?.status !== 'complete') {
          state[platePairGuid] = { status: 'pending', timestamp: action.payload.timestamp };
        }
      }
    },

    receiveError(state, action: PayloadAction<{ platePairGuids: string[]; timestamp: string }>) {
      for (const platePairGuid of action.payload.platePairGuids) {
        if (isPlateAnalysisStale(state, platePairGuid, action.payload.timestamp)) continue;

        state[platePairGuid] = { status: 'error', timestamp: action.payload.timestamp };
      }
    },

    receiveAnalysis(state, action: PayloadAction<{ analysis: PlateAnalysisResult; timestamp: string }>) {
      for (const [platePairGuid, plateAnalysis] of Object.entries(action.payload.analysis)) {
        if (isPlateAnalysisStale(state, platePairGuid, action.payload.timestamp)) continue;

        state[platePairGuid] = {
          status: 'complete',
          data: castDraft(plateAnalysis),
          timestamp: action.payload.timestamp,
        };
      }
    },
  },
  extraReducers: (builder) => {
    // reset livePlating cache when relevant
    builder.addCase(setRunPlateMislocations.type, () => emptyState);
    builder.addCase(undoComponentDesign.type, () => emptyState);
  },
});

function isPlateAnalysisStale(state: LivePlatingState, platePairGuid: string, timestamp: string) {
  const oldEntry = state[platePairGuid];
  const oldTimestamp = oldEntry?.timestamp;
  return oldTimestamp && oldTimestamp > timestamp;
}

export const livePlating = livePlatingSlice.reducer;
